import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Helmet } from "react-helmet";
import axios from 'axios';
import { Layout } from 'antd';
import { ArrowRight, Rocket, Shield, Users, Zap, Heart, Coffee, Globe, Lightbulb } from 'lucide-react';
import Loader from '../components/Loader';

const fadeInUp = {
    hidden: { opacity: 0, y: 60, filter: 'blur(10px)' },
    visible: {
        opacity: 1,
        y: 0,
        filter: 'blur(0px)',
        transition: { type: 'spring', stiffness: 100, damping: 20 }
    }
}

const About = () => {
    const [loading, setLoading] = useState(true)
    const [aboutData, setAboutData] = useState([])
    const [imageUrls, setImageUrls] = useState({})

    useEffect(() => {
        fetchMissionVisionData()
        window.scrollTo(0, 0);
    }, [])

    const fetchMissionVisionData = async () => {
        try {
            const response = await axios.get('pages/15');

            if (response.data && response.data.acf) {
                const missionVisionData = response.data.acf.about_us_box_home?.items_about_us_box_home || [];

                const imageIds = missionVisionData.map(item => item.image_about_us_home).filter(id => id);

                const imageRequests = imageIds.map(id =>
                    axios.get(`media/${id}`)
                        .then(res => ({ id, url: res.data.source_url }))
                        .catch(err => {
                            console.error(`Failed to fetch image ${id}:`, err);
                            return { id, url: null };
                        })
                );

                const imageResults = await Promise.all(imageRequests);

                const imageUrlMap = {};
                imageResults.forEach(result => {
                    imageUrlMap[result.id] = result.url;
                });

                setImageUrls(imageUrlMap);
                setAboutData(missionVisionData);
            }
            setLoading(false);
        } catch (err) {
            console.log('Error fetching mission/vision data:', err);
            setLoading(false);
        }
    };

    if (loading) {
        return <Loader />;
    }

    const milestones = [
        { year: "2020", title: "Company Founded", description: "Started with a vision to transform businesses" },
        { year: "2021", title: "First Major Client", description: "Onboarded first enterprise client" },
        { year: "2022", title: "Team Expansion", description: "Grew to 20+ talented professionals" },
        { year: "2024", title: "Innovation Hub", description: "Launched AI & ML Center of Excellence" }
    ];

    return (
        <>
            <Helmet>
                <title>About Us - Connected Bytes Software Service</title>
                <meta name="description" content="Learn about Connected Bytes Software Service - a leading software development company dedicated to delivering innovative technology solutions that transform businesses." />
                <meta property="og:title" content="About Us - Connected Bytes Software Service" />
                <meta property="og:description" content="Learn about Connected Bytes Software Service - a leading software development company." />
                <meta property="og:image" content="https://utfs.io/f/wzoc0SJJ8iMqmv8tcTUCV0hUWxjMFoDS38r1AKmZ5kftGwsN" />
                <meta property="og:url" content="https://connectedbytes.in/about" />
                <meta property="og:type" content="website" />
                <meta name="twitter:card" content="summary_large_image" />
            </Helmet>

            <div className="bg-background text-foreground min-h-screen transition-colors duration-300">
                <Layout className='bg-transparent'>
                    {/* Hero Section */}
                    <div className="relative pt-32 pb-24 overflow-hidden border-b border-border">
                        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/10 blur-[150px] rounded-full pointer-events-none" />
                        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-accent/10 blur-[150px] rounded-full pointer-events-none" />

                        <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
                            <motion.div
                                initial={{ opacity: 0, y: 30, filter: 'blur(10px)' }}
                                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                                transition={{ duration: 0.8, type: 'spring' }}
                            >
                                <span className="text-primary font-bold text-xs uppercase tracking-widest font-secondary block mb-4">Who We Are</span>
                                <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black font-heading mb-6 tracking-tight">
                                    About Connected Bytes
                                    <span className="block text-primary text-2xl sm:text-3xl md:text-4xl mt-2 font-bold">Software Service</span>
                                </h1>
                                <div className="w-24 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full mb-8 shadow-[0_0_15px_rgba(var(--color-primary),0.5)]" />
                                <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-body leading-relaxed">
                                    We are a team of passionate technologists dedicated to building elite
                                    software solutions that empower businesses to dominate and drive radical digital transformation.
                                </p>
                            </motion.div>
                        </div>
                    </div>

                    {/* Our Story Section */}
                    <div className="py-24 relative overflow-hidden">
                        <div className="max-w-7xl mx-auto px-4 relative z-10">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
                                <motion.div
                                    initial={{ opacity: 0, x: -50, filter: 'blur(10px)' }}
                                    whileInView={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                                    transition={{ duration: 0.8, type: 'spring' }}
                                    viewport={{ once: true }}
                                >
                                    <span className="text-primary font-bold text-xs uppercase tracking-widest font-secondary block mb-2">Our Story</span>
                                    <h2 className="text-4xl md:text-5xl font-black font-heading tracking-tight mb-4">Journey of Excellence</h2>
                                    <div className="w-16 h-1 bg-gradient-to-r from-primary to-accent rounded-full mb-8" />
                                    <p className="text-muted-foreground mb-6 leading-relaxed font-body text-lg">
                                        Connected Bytes Software Service was founded in 2020 with a simple yet powerful vision: to make bleeding-edge technology accessible to visionary businesses. What started as a focused team of passionate elite developers has evolved into a dynamic organization of tech experts, innovative designers, and strategic forward-thinkers.
                                    </p>
                                    <p className="text-muted-foreground mb-6 leading-relaxed font-body text-lg">
                                        Over the years, we've empowered 30+ forward-thinking clients across industries to revolutionize their digital presence, automate complex operations, and unlock scalable growth opportunities. Our approach bridges uncompromising technical excellence with deep strategic business understanding.
                                    </p>
                                    <p className="text-muted-foreground leading-relaxed font-body text-lg">
                                        Today, we continue to push boundaries, embracing emerging technologies and rapid methodologies to stay miles ahead of the curve. Our commitment to premium quality, radical transparency, and absolute client success remains the unyielding core of everything we build.
                                    </p>
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0, x: 50, filter: 'blur(10px)' }}
                                    whileInView={{ opacity: 1, x: 0, filter: 'blur(0px)' }}
                                    transition={{ duration: 0.8, type: 'spring' }}
                                    viewport={{ once: true }}
                                    className="relative"
                                >
                                    <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-border">
                                        <div className="absolute inset-0 bg-primary/10 opacity-0 hover:opacity-100 transition-opacity duration-500 z-10" />
                                        <img
                                            src="https://utfs.io/f/wzoc0SJJ8iMqmv8tcTUCV0hUWxjMFoDS38r1AKmZ5kftGwsN"
                                            alt="Team working together"
                                            className="w-full h-auto transform hover:scale-105 transition-transform duration-700"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-tr from-background/80 via-transparent to-transparent pointer-events-none" />
                                    </div>
                                    <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-accent/20 rounded-full blur-[80px] -z-10" />
                                    <div className="absolute -top-8 -left-8 w-40 h-40 bg-primary/20 rounded-full blur-[80px] -z-10" />
                                </motion.div>
                            </div>
                        </div>
                    </div>

                    {/* Mission & Vision Section */}
                    {aboutData.length > 0 && (
                        <div className="py-24 bg-muted/30 border-y border-border relative overflow-hidden">
                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-[500px] bg-primary/5 blur-[150px] rounded-full pointer-events-none" />
                            <div className="max-w-7xl mx-auto px-4 relative z-10">
                                <div className="text-center mb-16">
                                    <span className="text-primary font-bold text-xs uppercase tracking-widest font-secondary block mb-2">
                                        Our Mission & Vision
                                    </span>
                                    <h2 className="text-4xl md:text-5xl font-black font-heading mb-4">
                                        What Drives Us
                                    </h2>
                                    <div className="w-16 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full" />
                                    <p className="text-muted-foreground mt-6 max-w-2xl mx-auto font-body text-lg">
                                        Committed to pure excellence, defined by values that forge massive impact.
                                    </p>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                                    {aboutData.map((item, index) => {
                                        const imageId = item.image_about_us_home;
                                        const imageUrl = imageUrls[imageId];

                                        return (
                                            <motion.div
                                                key={index}
                                                initial={{ opacity: 0, y: 40, scale: 0.95 }}
                                                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                                                transition={{ duration: 0.6, delay: index * 0.1, type: "spring" }}
                                                viewport={{ once: true, margin: "-50px" }}
                                                className="bg-card/60 backdrop-blur-md rounded-2xl overflow-hidden shadow-sm hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] transition-all duration-500 border border-border group"
                                            >
                                                <div className="h-56 overflow-hidden relative">
                                                    {imageUrl ? (
                                                        <img
                                                            src={imageUrl}
                                                            alt={item.heading_about_us_home}
                                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                                            onError={(e) => {
                                                                e.target.onerror = null;
                                                                e.target.src = "https://via.placeholder.com/400x300?text=Image+Not+Found";
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-accent/10">
                                                            <span className="text-muted-foreground font-secondary text-sm">Loading visual...</span>
                                                        </div>
                                                    )}
                                                    <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent opacity-80" />
                                                </div>
                                                <div className="p-8 relative -mt-6 bg-card/90 backdrop-blur-sm rounded-t-3xl border-t border-border">
                                                    <h3 className="text-2xl font-bold font-heading mb-3 transition-colors group-hover:text-primary">
                                                        {item.heading_about_us_home}
                                                    </h3>
                                                    <div className="w-12 h-1 bg-gradient-to-r from-primary to-accent rounded-full mb-4" />
                                                    <p className="text-muted-foreground leading-relaxed font-body text-sm">
                                                        {item.writeup_about_us_home}
                                                    </p>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Core Values Section */}
                    <div className="py-24 relative overflow-hidden">
                        <div className="max-w-7xl mx-auto px-4 relative z-10">
                            <div className="text-center mb-16">
                                <span className="text-primary font-bold text-xs uppercase tracking-widest font-secondary block mb-2">Our Foundation</span>
                                <h2 className="text-4xl md:text-5xl font-black font-heading mb-4">Our Core Values</h2>
                                <div className="w-16 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full" />
                                <p className="text-muted-foreground mt-6 max-w-2xl mx-auto font-body text-lg">
                                    The indestructible principles that dictate our engineering philosophy and client relationships.
                                </p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                {[
                                    { icon: Rocket, title: "Innovation", desc: "Pushing technical boundaries seamlessly to deliver next-gen systems." },
                                    { icon: Shield, title: "Integrity", desc: "Absolute transparency, security by default, and unwavering honesty." },
                                    { icon: Users, title: "Collaboration", desc: "Partnering fiercely with clients to achieve synchronized goals." },
                                    { icon: Zap, title: "Excellence", desc: "Zero compromises. We relentlessly strive for architectural perfection." },
                                    { icon: Heart, title: "Passion", desc: "Deeply invested in code quality and aesthetic brilliance." },
                                    { icon: Coffee, title: "Dedication", desc: "We run the marathon for our clients' massive success." },
                                    { icon: Globe, title: "Global Mindset", desc: "Structuring solutions designed for planetary scale." },
                                    { icon: Lightbulb, title: "Creativity", desc: "Inventing elegant solutions to complex, multi-layered problems." }
                                ].map((value, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 30 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.5, delay: index * 0.05, type: 'spring' }}
                                        viewport={{ once: true, margin: '-50px' }}
                                        className="bg-card/40 backdrop-blur-sm border border-border rounded-2xl p-8 text-center shadow-sm hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] transition-all duration-300 group relative overflow-hidden"
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                        <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 group-hover:-rotate-3 transition-transform duration-300 relative z-10 shadow-inner">
                                            <value.icon className="w-8 h-8 text-primary group-hover:text-primary-foreground drop-shadow-sm transition-colors" />
                                        </div>
                                        <h3 className="text-xl font-bold font-heading mb-3 relative z-10 transition-colors group-hover:text-primary">{value.title}</h3>
                                        <p className="text-muted-foreground text-sm leading-relaxed font-body relative z-10">{value.desc}</p>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Milestones Timeline */}
                    <div className="py-24 bg-muted/30 border-y border-border overflow-hidden relative">
                        {/* Background Patterns */}
                        <div className="absolute inset-0 opacity-10 pointer-events-none">
                            <div className="absolute inset-0" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M54.627 0l.83.83v58.34h-58.34l-.83-.83L0 58.34V0h54.627zM29.17 30.83L30.83 29.17v-29.17h-2.5v29.17L29.17 30.83zM30.83 29.17L29.17 30.83h-29.17v-2.5h29.17L30.83 29.17z' fill='%23808080' fill-opacity='0.2' fill-rule='evenodd'/%3E%3C/svg%3E")` }}></div>
                        </div>

                        <div className="max-w-7xl mx-auto px-4 relative z-10">
                            <div className="text-center mb-20">
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5 }}
                                    viewport={{ once: true }}
                                >
                                    <span className="text-primary font-bold text-xs uppercase tracking-widest font-secondary block mb-2">Our Journey</span>
                                    <h2 className="text-4xl md:text-5xl font-black font-heading mb-4">
                                        Key Milestones
                                    </h2>
                                    <div className="w-16 h-1 bg-gradient-to-r from-primary to-accent mx-auto rounded-full mb-6" />
                                    <p className="text-muted-foreground max-w-2xl mx-auto font-body text-lg">
                                        A chronological trajectory of aggressive scaling and persistent innovation.
                                    </p>
                                </motion.div>
                            </div>

                            <div className="relative">
                                {/* Main Vertical Line Container */}
                                <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full hidden md:block">
                                    <div className="absolute inset-0 bg-border rounded-full"></div>
                                    <motion.div
                                        className="absolute top-0 left-0 right-0 bg-gradient-to-b from-primary via-accent to-primary rounded-full shadow-[0_0_15px_rgba(var(--color-primary),0.5)]"
                                        initial={{ height: "0%" }}
                                        whileInView={{ height: "100%" }}
                                        transition={{ duration: 2.5, ease: "easeOut" }}
                                        viewport={{ once: true, margin: "-100px" }}
                                    />
                                </div>

                                {milestones.map((milestone, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 80, filter: 'blur(5px)' }}
                                        whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                                        transition={{
                                            duration: 0.8,
                                            delay: index * 0.2,
                                            type: "spring",
                                        }}
                                        viewport={{ once: true, margin: "-50px" }}
                                        className={`relative flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                                            } items-center mb-24 last:mb-0 group`}
                                    >
                                        {/* Step Number Indicator */}
                                        <motion.div
                                            className="absolute left-1/2 transform -translate-x-1/2 -top-8 md:top-1/2 md:-translate-y-1/2 z-30 hidden md:block"
                                            initial={{ scale: 0, opacity: 0 }}
                                            whileInView={{ scale: 1, opacity: 1 }}
                                            transition={{ delay: index * 0.15, type: "spring", stiffness: 200 }}
                                            viewport={{ once: true }}
                                        >
                                            <div className="relative">
                                                <div className="w-14 h-14 rounded-full bg-background shadow-lg flex items-center justify-center border-2 border-primary transition-transform duration-500 group-hover:scale-110 group-hover:border-accent">
                                                    <motion.span
                                                        className="text-xl font-bold bg-gradient-to-br from-primary to-accent bg-clip-text text-transparent font-heading"
                                                        initial={{ scale: 0 }}
                                                        whileInView={{ scale: 1 }}
                                                        transition={{ delay: index * 0.2 }}
                                                        viewport={{ once: true }}
                                                    >
                                                        {index + 1}
                                                    </motion.span>
                                                </div>
                                                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 rounded-full bg-primary/20 blur-md -z-10 group-hover:bg-accent/30 transition-colors duration-500"></div>
                                            </div>
                                        </motion.div>

                                        {/* Content Card */}
                                        <div className={`w-full md:w-1/2 px-4 md:px-8 ${index % 2 === 0 ? 'md:pr-16' : 'md:pl-16'
                                            }`}>
                                            <motion.div
                                                whileHover={{
                                                    scale: 1.02,
                                                    y: -5,
                                                    transition: { duration: 0.3 }
                                                }}
                                                className="bg-card/80 backdrop-blur-md rounded-3xl p-8 shadow-sm hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] transition-all duration-300 border border-border relative overflow-hidden"
                                            >
                                                <div className="absolute top-4 right-4 md:hidden">
                                                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center border border-primary/20">
                                                        <span className="text-sm font-bold text-primary font-heading">{index + 1}</span>
                                                    </div>
                                                </div>

                                                <motion.div
                                                    className="absolute inset-0 bg-gradient-to-r from-primary/0 via-primary/5 to-accent/0"
                                                    initial={{ x: "-100%" }}
                                                    whileHover={{ x: "100%" }}
                                                    transition={{ duration: 0.8 }}
                                                />

                                                <div className="flex items-center gap-4 mb-6">
                                                    <motion.div
                                                        className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg"
                                                        initial={{ rotate: -180, scale: 0 }}
                                                        whileInView={{ rotate: 0, scale: 1 }}
                                                        transition={{ delay: index * 0.1, type: "spring", stiffness: 200 }}
                                                        viewport={{ once: true }}
                                                    >
                                                        <Rocket className="w-6 h-6 text-primary-foreground" />
                                                    </motion.div>
                                                    <div>
                                                        <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest block font-secondary">Chapter {index + 1}</span>
                                                        <span className="text-4xl font-black bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent font-heading drop-shadow-sm">
                                                            {milestone.year}
                                                        </span>
                                                    </div>
                                                </div>

                                                <h3 className="text-2xl font-bold font-heading mb-3 group-hover:text-primary transition-colors duration-300">
                                                    {milestone.title}
                                                </h3>

                                                <motion.div
                                                    className="w-16 h-1 bg-gradient-to-r from-primary to-accent rounded-full mb-4"
                                                    initial={{ width: 0 }}
                                                    whileInView={{ width: 64 }}
                                                    transition={{ delay: index * 0.1 + 0.3, duration: 0.5 }}
                                                    viewport={{ once: true }}
                                                />

                                                <p className="text-muted-foreground leading-relaxed font-body text-sm md:text-base">
                                                    {milestone.description}
                                                </p>
                                            </motion.div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* CTA Section */}
                    <div className="py-24 relative overflow-hidden border-b border-border bg-background">
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-accent/10" />
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-4xl h-[400px] bg-primary/20 blur-[120px] rounded-full pointer-events-none" />
                        <div className="max-w-4xl mx-auto text-center px-4 relative z-10">
                            <h2 className="text-4xl md:text-5xl font-black font-heading mb-6">Ready to Disrupt Your Market?</h2>
                            <p className="text-xl text-muted-foreground mb-10 font-body">Let's build a software architecture that destroys the competition.</p>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => window.location.href = '/contact'}
                                className="px-10 py-4 bg-primary text-primary-foreground rounded-full font-bold shadow-[0_0_20px_rgba(var(--color-primary),0.3)] hover:shadow-[0_0_30px_rgba(var(--color-primary),0.5)] transition-all inline-flex items-center gap-3 text-lg font-secondary"
                            >
                                Initiate Project <ArrowRight className="w-5 h-5" />
                            </motion.button>
                        </div>
                    </div>
                </Layout>
            </div>
        </>
    )
}

export default About